import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import { motion } from 'framer-motion'
import { ChevronRightIcon, ShieldCheckIcon, ArrowUpIcon } from '@heroicons/react/24/outline'

const sections = [
  { id: 'information-collection', number: 1, title: 'Information We Collect' },
  { id: 'how-we-use', number: 2, title: 'How We Use Your Information' },
  { id: 'information-sharing', number: 3, title: 'Information Sharing' },
  { id: 'data-security', number: 4, title: 'Data Security' },
  { id: 'your-rights', number: 5, title: 'Your Rights' },
  { id: 'cookies', number: 6, title: 'Cookies' },
  { id: 'changes', number: 7, title: 'Changes to This Policy' },
  { id: 'contact', number: 8, title: 'Contact Us' },
]

export default function PrivacyPage() {
  usePageTitle('Privacy Policy')
  const [activeSection, setActiveSection] = useState(sections[0].id)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const sectionRefs = useRef({})

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400)

      const scrollPosition = window.scrollY + 120
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = sectionRefs.current[sections[i].id]
        if (el && el.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id)
          break
        }
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToSection = (id) => {
    const el = sectionRefs.current[id]
    if (el) {
      const offset = 80
      const top = el.offsetTop - offset
      window.scrollTo({ top, behavior: 'smooth' })
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="py-8 sm:py-12"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm text-text-muted mb-6">
          <Link to="/" className="link-subtle hover:text-text">Home</Link>
          <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="text-text font-medium">Privacy Policy</span>
        </nav>

        {/* Header */}
        <div className="flex items-start gap-3.5 mb-8">
          <div className="rounded-xl bg-primary-50 dark:bg-primary-50 p-2.5 flex-shrink-0
            ring-4 ring-primary-50/50 dark:ring-primary-50/30">
            <ShieldCheckIcon className="h-6 w-6 text-primary-600 dark:text-primary-500" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-text leading-tight">Privacy Policy</h1>
            <p className="text-text-muted mt-1 text-sm">Last updated: March 2026</p>
          </div>
        </div>

        {/* Trust banner */}
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.25 }}
          className="mb-8 flex items-start gap-2.5 rounded-xl
            bg-green-50 dark:bg-green-50 border border-green-200 dark:border-green-200
            p-3.5 text-sm text-green-700 dark:text-green-600"
        >
          <ShieldCheckIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            We take your privacy seriously. We never sell your personal data to third parties.
          </p>
        </motion.div>

        <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-8">
          {/* Sidebar TOC — desktop only */}
          <aside className="hidden lg:block">
            <nav className="sticky top-24">
              <p className="text-xs font-bold uppercase tracking-wider text-text-muted mb-3">
                On this page
              </p>
              <ul className="space-y-0.5">
                {sections.map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left text-sm py-1.5 px-3 rounded-lg transition-all duration-150
                        ${activeSection === section.id
                          ? 'bg-primary-50 dark:bg-primary-50 text-primary-700 dark:text-primary-600 font-medium'
                          : 'text-text-muted hover:text-text hover:bg-surface-100 dark:hover:bg-surface-200'
                        }`}
                    >
                      {section.number}. {section.title}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Content */}
          <div className="card p-6 sm:p-8 lg:p-10">
            {/* Mobile TOC */}
            <details className="lg:hidden mb-8 group">
              <summary className="flex items-center justify-between cursor-pointer
                text-sm font-semibold text-text p-3 rounded-lg
                bg-surface-50 dark:bg-surface-100 border border-border
                hover:bg-surface-100 dark:hover:bg-surface-200 transition-colors">
                <span>Table of Contents</span>
                <ChevronRightIcon className="h-4 w-4 text-text-muted transition-transform duration-200
                  group-open:rotate-90" />
              </summary>
              <nav className="mt-2 pl-3 border-l-2 border-border">
                <ul className="space-y-1 py-2">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => scrollToSection(section.id)}
                        className="text-sm text-text-secondary hover:text-primary-600 dark:hover:text-primary-500
                          py-1 transition-colors w-full text-left"
                      >
                        {section.number}. {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </details>

            <div className="prose-app space-y-10">
              <section ref={(el) => (sectionRefs.current['information-collection'] = el)} id="information-collection">
                <h2 className="text-lg font-bold text-text mb-3 flex items-baseline gap-2">
                  <span className="text-primary-500 dark:text-primary-500 font-mono text-base">01</span>
                  Information We Collect
                </h2>
                <p>
                  We collect information that you provide directly to us, including but not limited to your name, email address, and any other information you choose to provide.
                </p>
              </section>

              <div className="border-t border-border/60" />

              <section ref={(el) => (sectionRefs.current['how-we-use'] = el)} id="how-we-use">
                <h2 className="text-lg font-bold text-text mb-3 flex items-baseline gap-2">
                  <span className="text-primary-500 dark:text-primary-500 font-mono text-base">02</span>
                  How We Use Your Information
                </h2>
                <p>
                  We use the information we collect to provide, maintain, and improve our services, to communicate with you, and to comply with legal obligations.
                </p>
              </section>

              <div className="border-t border-border/60" />

              <section ref={(el) => (sectionRefs.current['information-sharing'] = el)} id="information-sharing">
                <h2 className="text-lg font-bold text-text mb-3 flex items-baseline gap-2">
                  <span className="text-primary-500 dark:text-primary-500 font-mono text-base">03</span>
                  Information Sharing
                </h2>
                <p>
                  We do not share your personal information with third parties except as described in this privacy policy. We may share your information with service providers who perform services on our behalf.
                </p>
              </section>

              <div className="border-t border-border/60" />

              <section ref={(el) => (sectionRefs.current['data-security'] = el)} id="data-security">
                <h2 className="text-lg font-bold text-text mb-3 flex items-baseline gap-2">
                  <span className="text-primary-500 dark:text-primary-500 font-mono text-base">04</span>
                  Data Security
                </h2>
                <p>
                  We take reasonable measures to help protect your personal information from loss, theft, misuse, unauthorized access, disclosure, alteration, and destruction.
                </p>
              </section>

              <div className="border-t border-border/60" />

              <section ref={(el) => (sectionRefs.current['your-rights'] = el)} id="your-rights">
                <h2 className="text-lg font-bold text-text mb-3 flex items-baseline gap-2">
                  <span className="text-primary-500 dark:text-primary-500 font-mono text-base">05</span>
                  Your Rights
                </h2>
                <p>
                  You have the right to access, correct, or delete your personal information. You may also have the right to restrict or object to certain processing of your information.
                </p>
              </section>

              <div className="border-t border-border/60" />

              <section ref={(el) => (sectionRefs.current['cookies'] = el)} id="cookies">
                <h2 className="text-lg font-bold text-text mb-3 flex items-baseline gap-2">
                  <span className="text-primary-500 dark:text-primary-500 font-mono text-base">06</span>
                  Cookies
                </h2>
                <p>
                  We use cookies and similar tracking technologies to track activity on our website and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
                </p>
              </section>

              <div className="border-t border-border/60" />

              <section ref={(el) => (sectionRefs.current['changes'] = el)} id="changes">
                <h2 className="text-lg font-bold text-text mb-3 flex items-baseline gap-2">
                  <span className="text-primary-500 dark:text-primary-500 font-mono text-base">07</span>
                  Changes to This Policy
                </h2>
                <p>
                  We may update this privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page.
                </p>
              </section>

              <div className="border-t border-border/60" />

              <section ref={(el) => (sectionRefs.current['contact'] = el)} id="contact">
                <h2 className="text-lg font-bold text-text mb-3 flex items-baseline gap-2">
                  <span className="text-primary-500 dark:text-primary-500 font-mono text-base">08</span>
                  Contact Us
                </h2>
                <p>
                  If you have any questions about this privacy policy, please contact us at{' '}
                  <a href="mailto:privacy@appcerc.com" className="link">privacy@appcerc.com</a>.
                </p>
              </section>
            </div>
          </div>
        </div>

        {/* Footer link */}
        <div className="mt-8 text-center">
          <Link to="/terms" className="link text-sm inline-flex items-center gap-1.5">
            View our Terms of Service
            <ChevronRightIcon className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      {/* Back to top button */}
      {showBackToTop && (
        <motion.button
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 z-sticky
            p-3 rounded-full bg-primary-600 text-white shadow-lg
            hover:bg-primary-700 active:scale-95
            transition-all duration-200
            min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Back to top"
        >
          <ArrowUpIcon className="h-5 w-5" />
        </motion.button>
      )}
    </motion.div>
  )
}
